/**
 * Crew: "Invite crew" is one tap — it makes a join link and opens the phone's share sheet
 * (navigator.share) so it goes out by Messages / WhatsApp / mail; where there is no share sheet the
 * link is copied (and shown to copy by hand). Below: the crew call sheet (the same one as the TEAM
 * tab) — who, job, camera, place, status — assigned in two taps, and "Someone without the app".
 */
import { useMemo, useState } from 'react';
import type { MissionBundle, Role } from '@apos/shared';
import { Button, Segmented, toast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { atLeast, ROLE_LABEL } from '../../app/labels';
import { getNative } from '../../lib/native';
import { publicUrl } from '../../lib/publicUrl';
import { useLiveWorld } from '../../state/world';
import { AddPersonForm, CallSheet } from '../team/CallSheet';
import { buildTeam } from '../team/teamModel';
import '../team/team.css';
import './setup.css';
import { Disclosure } from './FormBits';

export type InviteRole = Exclude<Role, 'owner'>;

export const inviteLink = (code: string) => publicUrl(`/join/${code}`);

/**
 * Share a join link: the native share sheet when there is one, else copy. Resolves to how it went
 * ('shared' | 'copied' | 'shown' when even copying failed | 'cancelled').
 */
export async function shareInviteLink(link: string, missionName: string, nav: Navigator = navigator): Promise<'shared' | 'copied' | 'shown' | 'cancelled'> {
  const text = `Join "${missionName}" on Airview Studio`;
  const native = getNative();
  if (native?.share) {
    native.share({ title: 'Airview Studio invite', text, url: link });
    return 'shared';
  }
  if (typeof nav.share === 'function') {
    try {
      await nav.share({ title: 'Airview Studio invite', text, url: link });
      return 'shared';
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return 'cancelled';
      // fall through to copy (e.g. share not allowed here)
    }
  }
  try {
    await nav.clipboard.writeText(link);
    return 'copied';
  } catch {
    return 'shown';
  }
}

/** The invite button + the link it made (role picker tucked under "Invite as"). */
export function InviteCrew({ bundle, compact }: { bundle: MissionBundle; compact?: boolean }) {
  const [role, setRole] = useState<InviteRole>('crew');
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invite = async () => {
    setBusy(true);
    try {
      // one link per role per sheet: re-sharing reuses it
      const l = link ?? inviteLink((await api.missions.invite(bundle.mission.id, { role, expiresInHours: 72 })).code);
      setLink(l);
      const how = await shareInviteLink(l, bundle.mission.name);
      if (how === 'copied') toast({ title: 'Invite link copied', body: 'Paste it in a message to the crew.', tone: 'success' });
      if (how === 'shown') toast({ title: 'Copy the link below and send it to the crew' });
    } catch (e) {
      toast({ title: 'Could not create the invite', body: errorMessage(e), tone: 'alert' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="crew-invite stack-12">
      <Button variant="primary" size="lg" block icon="users" loading={busy} onClick={() => void invite()} data-testid="invite-crew">
        {link ? 'Share the invite again' : role === 'crew' ? 'Invite crew' : `Invite a ${ROLE_LABEL[role].toLowerCase()}`}
      </Button>
      {link ? (
        <div className="crew-invite__link">
          <span className="num" data-testid="invite-link">
            {link}
          </span>
          <Button
            size="sm"
            icon="copy"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(link);
                toast({ title: 'Invite link copied', tone: 'success' });
              } catch {
                toast({ title: 'Copy failed — select the link and copy it' });
              }
            }}
          >
            Copy
          </Button>
        </div>
      ) : null}
      {!compact || link ? (
        <Disclosure title={`Invite as ${ROLE_LABEL[role].toLowerCase()}`} hint="The link works for 72 h">
          <Segmented<InviteRole>
              block
              size="sm"
              label="Invite as"
              value={role}
              onChange={(r) => {
                setRole(r);
                setLink(null);
              }}
              options={[
                { value: 'crew', label: 'Crew' },
                { value: 'producer', label: 'Producer' },
                { value: 'viewer', label: 'Viewer' },
              ]}
            />
        </Disclosure>
      ) : null}
    </div>
  );
}

/** Who is on the shoot: the call sheet (members and people without the app), plus "Someone without the app". */
export function CrewRoster({ bundle }: { bundle: MissionBundle }) {
  // status (live / not sharing / no app) is enough at a slow tick here
  const world = useLiveWorld(0.5);
  const rows = useMemo(() => buildTeam(bundle, world), [bundle, world]);
  const [adding, setAdding] = useState(false);
  const canAdd = atLeast(bundle.myRole, 'producer');
  return (
    <div className="stack-12" data-testid="crew-roster">
      <CallSheet bundle={bundle} rows={rows} />
      {canAdd ? (
        adding ? (
          <AddPersonForm bundle={bundle} onDone={() => setAdding(false)} />
        ) : (
          <Button icon="plus" variant="secondary" className="crew-roster__add" onClick={() => setAdding(true)} data-testid="crew-add-person">
            Someone without the app
          </Button>
        )
      ) : null}
    </div>
  );
}

export function CrewPanel({ bundle, canInvite }: { bundle: MissionBundle; canInvite: boolean }) {
  return (
    <div className="setup-form stack-16">
      {canInvite ? <InviteCrew bundle={bundle} compact /> : null}
      <CrewRoster bundle={bundle} />
    </div>
  );
}
